import React, { useMemo, useState } from 'react';
import { Box, emailValidation, Field, FieldTypes } from '@common-fe/common-fe';
import * as yup from 'yup';

import { REQUIRED_TEXT } from '@/common/constants';
import { useGeneralInfoStore } from '@/modules/employee/Employee/PersonalInformation/GeneralInfo/stores';

import ResendVerificationLink from '../components/ResendVerificationLink';
import VerificationHint from '../components/VerificationHint';

const VALIDATOR = yup.string().trim().nullable();

interface Props {
  isEditMode?: boolean;
}

const useEmployeeEmailField = ({ isEditMode }: Props) => {
  const [employeeEmailVerificationErrorText, setEmployeeEmailVerificationErrorText] = useState<string | undefined>(undefined);
  const defaultFields = useGeneralInfoStore((state) => state.sourceState);
  const isEmployeeEmailVerified = useGeneralInfoStore((state) => state.state.isEmployeeEmailVerified);
  const employerEmail = useGeneralInfoStore((state) => state.state.employerEmail);

  const showResendVerificationLink = useMemo(() => defaultFields?.employeeEmail
    && defaultFields?.employeeId && !isEmployeeEmailVerified && !isEditMode,
  [defaultFields, isEmployeeEmailVerified, isEditMode]);

  const showVerificationHint = useMemo(() => !!defaultFields?.employeeEmail, [defaultFields?.employeeEmail]);

  const employeeEmailField: Field = useMemo(() => ({
    name: 'employeeEmail',
    type: FieldTypes.Text,
    label: 'Employee provided email',
    disabled: !isEditMode,
    placeholder: 'Enter email',
    fill: !isEditMode,
    fieldWrapperStyles: {
      flexDirection: 'row',
    },
    ...(showVerificationHint ? {
      rightIcon: <VerificationHint isVerified={isEmployeeEmailVerified} />,
    } : {}),
    ...(showResendVerificationLink || showVerificationHint && !isEditMode ? {
      fieldChildren: (
        <Box height="40px" direction="row" align="center" margin={{ left: 'spacing4' }}>
          <VerificationHint isVerified={isEmployeeEmailVerified} />
          {showResendVerificationLink && (
            <Box margin={{ left: 'spacing16' }}>
              <ResendVerificationLink
                valueKey={`${defaultFields?.employeeId}_${defaultFields?.employeeEmail}`}
                employeeId={defaultFields?.employeeId as string}
                value={defaultFields?.employeeEmail as string}
                setVerificationErrorText={setEmployeeEmailVerificationErrorText}
              />
            </Box>
          )}
        </Box>
      ),
    } : {}),
    rightIconWrapperProps: { style: { opacity: 1 } },
    validator: isEditMode
      ? emailValidation.test({
        test: (value) => Boolean(value || employerEmail),
        message: REQUIRED_TEXT,
      }) : VALIDATOR,
    ...(employeeEmailVerificationErrorText ? { manualError: employeeEmailVerificationErrorText } : {}),
  }), [defaultFields, employerEmail, employeeEmailVerificationErrorText,
    isEmployeeEmailVerified, isEditMode, showResendVerificationLink, showVerificationHint]);

  return employeeEmailField;
};

export default useEmployeeEmailField;
